import { useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ReactComponent as EditIcon } from "assets/images/common/edit.svg";
import { ReactComponent as TrashIcon } from "assets/images/common/trash.svg";

import { LoaderContext } from "context/GlobalLoader";

import useRealtime from "hooks/useRealtime";

import { ModalType, UserRole } from "entities/common";
import { UpdateCompanyRequest } from "entities/companies";
import PageTemplate from "modules/common/PageTemplate";
import StatCard, { StatCardData } from "modules/common/StatCard";
import { COMPANY_COUNTRY } from "utils/data/emirates";
import { CompanyFormValues } from "widgets/Modal/CreateCompany/Content";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getCompanyRequest,
  getCompanyStatisticsRequest,
  resetSuccessfullyUpdatedCompany,
  updateCompanyRequest,
} from "store/slices/companiesSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { showModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";

import Button from "components/Button";
import Card from "components/Card";
import Tabs from "components/Tabs";

import CompanyOverviewTab from "modules/companies/OverviewTab";
import CompanyPortfoliosTab from "modules/companies/PortfoliosTab";
import CompanyUsersTab from "modules/companies/UsersTab";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import styles from "./style.module.scss";

const formatImprovement = (value: number | null | undefined): string => {
  if (value == null) return "-";
  if (value > 0) return `+${value}%`;
  return `${value}%`;
};

const CompanyDetailPage = () => {
  const { id } = useParams();
  const callbackKey = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { handleLoad } = useContext(LoaderContext);
  const { company, isCompanyLoading, successfullyUpdatedId, companyStatistics } = useAppSelector(
    (state: RootStore) => state.companies,
  );
  const { user } = useAppSelector((state: RootStore) => state.auth);

  const { [QUERY_PARAMS.TAB]: tab } = QueryHelper.getFromQueryString(location.search);
  const [activeTab, setActiveTab] = useState(tab || "overview");

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      navigate(
        `${PAGES.COMPANY_DETAIL.replace(":id", id?.toString() || "")}${QueryHelper.getQueryString({ [QUERY_PARAMS.TAB]: tab })}`,
      );
    },
    [navigate, id],
  );

  const handleUpdateCompany = useCallback(
    (id: number) => (values?: unknown) => {
      const data = values as CompanyFormValues;
      const body: UpdateCompanyRequest = {
        company_name: data.companyName,
        country: COMPANY_COUNTRY,
        city: data.emirate,
      };

      dispatch(updateCompanyRequest({ id, body }));
    },
    [dispatch],
  );

  const handleEditCompany = () => {
    setModalCallbacks(callbackKey, { onSave: handleUpdateCompany(Number(id)) });

    dispatch(
      showModal({
        modalType: ModalType.CREATE_COMPANY,
        modalProps: {
          mode: "edit",
          id: Number(id),
          initialData: {
            companyName: company?.name || "",
            emirate: company?.city?.label || "",
            fullName: company?.owner?.full_name || "",
            email: company?.owner?.email || "",
            position: company?.owner?.position || "",
            phoneNumber: company?.owner?.phone || "",
          },
          callbackKey,
        },
      }),
    );
  };

  const handleDeleteCompany = () => {
    dispatch(
      showModal({
        modalType: ModalType.REMOVE_COMPANY,
        modalProps: {
          id: id,
          name: company?.name || "",
          portfolios: company?.portfolio_count.toString() || "",
          assets: company?.asset_count.toString() || "",
          users: company?.user_count.toString() || "",
        },
      }),
    );
  };

  const cards: StatCardData[] = useMemo(() => {
    const level = companyStatistics?.compliance_score_level;
    const improvementLevel = companyStatistics?.compliance_improvement_level;
    const avgIncidents = companyStatistics?.avg_incidents_per_month;

    return [
      {
        title: "Compliance score",
        value: level && level !== "insufficient" ? `${companyStatistics?.compliance_score}%` : "-",
        description:
          level && level !== "insufficient"
            ? `${level === "high" ? "High" : level === "medium" ? "Medium" : "Low"} compliance`
            : "Insufficient data",
        icon: "shield",
        tone: level === "high" ? "green" : level === "medium" ? "orange" : level === "low" ? "red" : "gray",
        tooltip: "Per DM-HSD-GU119.",
      },
      {
        title: "Compliance improvement",
        value:
          improvementLevel && improvementLevel !== "insufficient"
            ? formatImprovement(companyStatistics?.compliance_improvement)
            : "-",
        description:
          !improvementLevel || improvementLevel === "insufficient" ? "Insufficient data" : "Vs previous 30 days",
        icon:
          improvementLevel === "up"
            ? "grow-up"
            : improvementLevel === "down"
              ? "grow-down"
              : improvementLevel === "flat"
                ? "flat"
                : undefined,
        tone: improvementLevel === "up" ? "green" : improvementLevel === "down" ? "red" : "gray",
        tooltip:
          "Change in the company compliance score compared to the previous 30 days, across all portfolios in this company.",
      },
      {
        title: "Avg incidents/month",
        value: avgIncidents == null ? "-" : `${avgIncidents}`,
        description: avgIncidents == null ? "Insufficient data" : "Last 3 months",
        icon: "danger",
        tone: avgIncidents == null ? "gray" : avgIncidents === 0 ? "green" : avgIncidents < 4 ? "orange" : "red",
      },
    ];
  }, [
    companyStatistics?.compliance_score,
    companyStatistics?.compliance_score_level,
    companyStatistics?.compliance_improvement,
    companyStatistics?.compliance_improvement_level,
    companyStatistics?.avg_incidents_per_month,
  ]);

  const tabs = useMemo(() => {
    return [
      {
        label: "Overview",
        value: "overview",
      },
      {
        label: "Users",
        value: "users",
        notification: company?.user_count,
      },
      {
        label: "Portfolios",
        value: "portfolios",
        notification: company?.portfolio_count,
      },
    ];
  }, [company?.user_count, company?.portfolio_count]);

  // get company
  useEffect(() => {
    dispatch(getCompanyRequest(Number(id)));
  }, [dispatch, id]);

  // show loader
  useEffect(() => {
    handleLoad(isCompanyLoading);

    return () => {
      handleLoad(false);
    };
  }, [handleLoad, isCompanyLoading]);

  // redirect to forbidden page if user is not administrator
  useEffect(() => {
    if (!!user?.role && user?.role !== UserRole.ADMINISTRATOR) {
      navigate(PAGES.FORBIDDEN);
    }
  }, [user?.role]);

  // successfully updated company
  useEffect(() => {
    if (successfullyUpdatedId) {
      dispatch(
        showToast({
          id: "successfully-updated-company",
          message: "The company was successfully updated.",
          type: "success",
          duration: 3000,
        }),
      );
      dispatch(resetSuccessfullyUpdatedCompany());
    }
  }, [successfullyUpdatedId, dispatch]);

  // get company statistics
  useEffect(() => {
    dispatch(getCompanyStatisticsRequest(Number(id)));
  }, [dispatch, id]);

  const reload = useCallback(() => {
    dispatch(getCompanyStatisticsRequest(Number(id), { silent: true }));
  }, [dispatch, id]);

  useRealtime(["compliance", "incidents"], reload, { companyId: Number(id) });

  return (
    <PageTemplate
      isBackButton
      backButtonLink={PAGES.COMPANY_LIST}
      title={company?.name || ""}
      actions={
        <div className={styles.detailActions}>
          <Button variant="white" form="circle" onClick={handleEditCompany}>
            <EditIcon />
          </Button>

          <Button variant="white" form="circle" onClick={handleDeleteCompany}>
            <TrashIcon />
          </Button>
        </div>
      }
      className={styles.detail}
    >
      <>
        <div className={styles.detailCards}>
          {cards.map((card) => (
            <StatCard key={card.title} data={card} />
          ))}
        </div>

        <Card className={styles.detailTabs}>
          <Tabs tabs={tabs} value={activeTab} onChange={handleTabChange} />

          <div className={styles.detailTabsContent}>
            {activeTab === "overview" && <CompanyOverviewTab company={company} />}
            {activeTab === "users" && <CompanyUsersTab />}
            {activeTab === "portfolios" && <CompanyPortfoliosTab />}
          </div>
        </Card>
      </>
    </PageTemplate>
  );
};

export default CompanyDetailPage;
